import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

import {
  LOCALE_BADGE,
  SUPPORTED_LOCALES,
  type Locale,
} from "@pervasivemind/dashboard-render";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";

/**
 * Synonyms shape stored on KPI / chart / column / filter configs.
 *
 * Mirror of the Python ``Synonyms`` dataclass in
 * ``report-engine/models.py``. Per-locale lists of alternative terms
 * the chat LLM should treat as referring to the same canonical entity
 * (e.g. ``synonyms.es=["ventas","facturación"]`` lets a user prompt
 * "muéstrame las ventas" resolve to ``total_sales``).
 *
 * Empty arrays / undefined = no synonyms.
 */
export interface Synonyms {
  en?: string[];
  es?: string[];
  [locale: string]: string[] | undefined;
}

interface SynonymsEditorProps {
  /** Current value. ``undefined`` / ``null`` treated as empty per locale. */
  value: Synonyms | undefined | null;
  /** Called with the new shape after every chip add/remove. */
  onChange: (next: Synonyms) => void;
  /** Disable both editors (read-only mode). */
  disabled?: boolean;
  /**
   * Locales to surface as side-by-side chip editors. Defaults to
   * ``SUPPORTED_LOCALES`` (``["en", "es"]``). Following the
   * ``BilingualInput`` pattern — adding ``pt`` to ``i18n-resolve.ts``
   * picks it up here for free.
   */
  locales?: ReadonlyArray<Locale>;
}

/**
 * Side-by-side chip editor for per-locale synonyms.
 *
 * Each locale gets its own input field — typing a term and pressing
 * Enter (or comma) adds it as a chip. Click the ``×`` on a chip to
 * remove it. Empty submissions are ignored; duplicates (case-insensitive
 * per locale) are filtered out silently.
 *
 * Storage shape mirrors the Python ``Synonyms`` dataclass: the parent
 * receives ``{en: [...], es: [...]}`` after every change. Empty locales
 * are pruned so a no-synonyms state round-trips as ``{}``.
 */
export function SynonymsEditor({
  value,
  onChange,
  disabled,
  locales,
}: SynonymsEditorProps): JSX.Element {
  const { t } = useT();
  const activeLocales = locales ?? SUPPORTED_LOCALES;

  // Local mirror of the input drafts (one per locale) so typing stays
  // responsive without re-rendering the parent on every keystroke.
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Local mirror of the chip list so add/remove is responsive even
  // before the parent's onChange round-trip lands. Re-syncs when
  // ``value`` changes externally (e.g. loading a different draft).
  const [chips, setChips] = useState<Synonyms>(() => normalize(value));
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    setChips(normalize(value));
  }, [value]);

  const update = (lang: Locale, terms: string[]): void => {
    // Strip empties + de-dupe case-insensitive while preserving order
    // and original casing of the first occurrence.
    const seen = new Set<string>();
    const cleaned: string[] = [];
    for (const term of terms) {
      const trimmed = term.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      cleaned.push(trimmed);
    }
    const next: Synonyms = { ...chips, [lang]: cleaned };
    // Prune empty locales so an admin who cleared all chips leaves
    // ``{}`` on disk instead of ``{en: [], es: []}``.
    for (const k of Object.keys(next)) {
      if ((next[k] ?? []).length === 0) delete next[k];
    }
    setChips(next);
    onChange(next);
  };

  const addChip = (lang: Locale): void => {
    const raw = draft[lang] ?? "";
    if (!raw.trim()) return;
    // A single keystroke may contain comma-separated terms — split
    // them out so "ventas, facturación" pastes as two chips.
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    update(lang, [...(chips[lang] ?? []), ...parts]);
    setDraft((prev) => ({ ...prev, [lang]: "" }));
    inputRefs.current[lang]?.focus();
  };

  const removeChip = (lang: Locale, idx: number): void => {
    const next = [...(chips[lang] ?? [])];
    next.splice(idx, 1);
    update(lang, next);
  };

  const onKeyDown = (
    lang: Locale,
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addChip(lang);
      return;
    }
    // Backspace on an empty draft removes the last chip (matches the
    // ergonomic of every other chip input on the web).
    if (
      e.key === "Backspace" &&
      !(draft[lang] ?? "") &&
      (chips[lang] ?? []).length > 0
    ) {
      e.preventDefault();
      removeChip(lang, (chips[lang] ?? []).length - 1);
    }
  };

  const gridCols =
    activeLocales.length === 1
      ? "grid-cols-1"
      : "grid-cols-1 sm:grid-cols-2";

  return (
    <div className={cn("grid gap-2", gridCols)}>
      {activeLocales.map((lang) => {
        const langChips = chips[lang] ?? [];
        return (
          <div
            key={lang}
            className="flex flex-col gap-1 rounded-md border border-neutral-200 bg-neutral-50/50 p-1.5"
          >
            <div className="flex items-center gap-1">
              <span
                className="rounded bg-neutral-200 px-1.5 text-xs font-semibold text-neutral-600"
                title={lang}
              >
                {LOCALE_BADGE[lang] ?? lang.toUpperCase()}
              </span>
              <input
                ref={(el) => {
                  inputRefs.current[lang] = el;
                }}
                value={draft[lang] ?? ""}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, [lang]: e.target.value }))
                }
                onKeyDown={(e) => onKeyDown(lang, e)}
                onBlur={() => addChip(lang)}
                placeholder={t("editor.synonyms.inputPlaceholder")}
                disabled={disabled}
                className="block min-w-0 flex-1 border-0 bg-transparent px-1 py-2 text-xs placeholder:text-neutral-500 focus:outline-none focus:ring-0"
              />
            </div>
            {langChips.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {langChips.map((chip, i) => (
                  <span
                    key={`${chip}-${i}`}
                    className="inline-flex items-center gap-1 rounded-full bg-info/15 px-2 py-0.5 text-xs font-medium text-info-dark"
                  >
                    {chip}
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => removeChip(lang, i)}
                        className="grid h-3.5 w-3.5 place-items-center rounded-full text-info-dark hover:bg-info/25"
                        title={t("editor.synonyms.removeChip")}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function normalize(value: Synonyms | null | undefined): Synonyms {
  if (!value || typeof value !== "object") return {};
  const out: Synonyms = {};
  for (const lang of Object.keys(value)) {
    const raw = value[lang];
    if (Array.isArray(raw)) {
      const cleaned = raw
        .map((x) => (typeof x === "string" ? x.trim() : ""))
        .filter(Boolean);
      if (cleaned.length > 0) out[lang] = cleaned;
    }
  }
  return out;
}
